import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { splitPacksForTenant } from "@/lib/renata/experience/niche";
import type { ExpertisePack } from "@/lib/renata/packs/types";

const PACK_NAME_ES: Record<string, string> = {
  restaurant: "Restaurant",
  bar: "Bar",
  cafeteria: "Cafetería",
  vinoteca: "Vinoteca",
  carniceria: "Carnicería",
  pet_shop: "Pet shop",
  farmacia: "Farmacia",
  ferreteria: "Ferretería",
  kiosco: "Kiosco",
  moda: "Moda",
  salud: "Salud",
  servicios: "Servicios",
};

export const Route = createFileRoute("/app/renata/packs")({
  component: PacksPage,
  head: () => ({ meta: [{ title: "Configuración del negocio — Renata" }] }),
});

function PacksPage() {
  const tenantId = useWorkspaceTenantId();
  const [packs, setPacks] = useState<ExpertisePack[]>([]);
  const [niche, setNiche] = useState<string | null>(null);
  const [showOthers, setShowOthers] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    void fetch("/api/renata/planning?view=packs", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    })
      .then((r) => r.json())
      .then((d) => {
        setPacks(d.packs ?? []);
        setNiche(d.tenantNiche ?? d.niche ?? null);
      });
  }, [tenantId]);

  const { primary, others } = useMemo(
    () => splitPacksForTenant(packs, niche),
    [packs, niche],
  );
  const visible = showOthers ? [...primary, ...others] : primary.length ? primary : packs;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <RenataPageChrome
        screenId="packs"
        title="Configuración del negocio"
        subtitle="Renata se adapta a tu rubro. Primero ves el tuyo."
      />

      <div className="space-y-3">
        {visible.map((p) => (
          <div key={p.id} className="rounded-lg border p-4 space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">{PACK_NAME_ES[p.id] ?? p.name}</h3>
              {primary.some((x) => x.id === p.id) ? (
                <Badge>Tu rubro</Badge>
              ) : (
                <Badge variant="outline">Otro tipo</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{p.description}</p>
          </div>
        ))}
      </div>

      {others.length > 0 ? (
        <Button variant="outline" size="sm" onClick={() => setShowOthers((v) => !v)}>
          {showOthers ? "Ocultar otros tipos de negocio" : "Ver otros tipos de negocio"}
        </Button>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Tu siguiente paso es confirmar tu rubro o explorar otras configuraciones.
      </p>
    </div>
  );
}
